import { PLATFORM_LABELS, type Platform, type PlatformKind } from '@shopx-distribution/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  useAuthorizeConnection,
  useConnections,
  useDisconnect,
  useEnablePlatform,
  type PlatformOption,
} from '@/api/hooks.js';
import { OAUTH_RESULT_MESSAGE, type OAuthResultMessage } from '@/admin/OAuthCompletePage.js';
import { PlatformIcon } from '@/components/PlatformIcon.js';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Loading,
  SectionHeading,
  StatusBadge,
} from '@/components/ui.js';
import { formatDateTime } from '@/lib/utils.js';

/**
 * Connect and disconnect the systems this extension can send creatives to.
 *
 * The OAuth flow leaves the iframe entirely: the authorize URL is opened at the
 * top level because these providers refuse to render their consent screens
 * inside a third-party frame. The API's callback then redirects back here.
 */
export function ConnectionsPage() {
  const connections = useConnections();
  const authorize = useAuthorizeConnection();
  const enable = useEnablePlatform();
  const disconnect = useDisconnect();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  // The popup relays the callback's outcome back here when it lands.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as OAuthResultMessage | undefined;
      if (data?.type !== OAUTH_RESULT_MESSAGE) return;

      if (data.error) {
        setNotice(`Could not connect the account: ${data.error}`);
        return;
      }
      setNotice('Account connected. Add a target to start distributing.');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [queryClient]);

  const connect = async (option: PlatformOption) => {
    if (option.connectionMode === 'instant') {
      await enable.mutateAsync(option.platform);
      setNotice(`${option.label} is enabled and ready to receive creatives.`);
      return;
    }

    const { authorizationUrl } = await authorize.mutateAsync({ platform: option.platform });
    // `noopener` is deliberately omitted: the popup needs `window.opener` to
    // report back, and both ends are on this app's own origin.
    window.open(authorizationUrl, 'distribution-oauth');
  };

  if (connections.isLoading) return <Loading label="Loading connections…" />;
  if (connections.error) return <ErrorNotice error={connections.error} />;

  const platforms = connections.data?.platforms ?? [];
  const connected = new Set(connections.data?.connections.map((one) => one.platform));
  const ofKind = (kind: PlatformKind) => platforms.filter((one) => one.kind === kind);

  return (
    <div className="space-y-8">
      {notice ? (
        <div className="rounded-brand border border-line bg-surface p-4 text-sm">{notice}</div>
      ) : null}

      <PlatformGrid
        title="Ad platforms"
        description="Connecting opens the platform's consent screen in a new tab."
        options={ofKind('ad_platform')}
        connected={connected}
        busy={authorize.isPending || enable.isPending}
        onConnect={connect}
      />

      <PlatformGrid
        title="DAM systems"
        description="The systems already available as GraFx media connectors, so creatives can go back to where the assets came from."
        options={ofKind('dam')}
        connected={connected}
        busy={authorize.isPending || enable.isPending}
        onConnect={connect}
      />

      {authorize.error ? <ErrorNotice error={authorize.error} /> : null}
      {enable.error ? <ErrorNotice error={enable.error} /> : null}

      <section>
        <SectionHeading title="Connected accounts" />
        {connections.data?.connections.length === 0 ? (
          <EmptyState
            title="Nothing connected"
            description="Connect a destination above to make it available as a target."
          />
        ) : null}

        <div className="grid gap-2">
          {connections.data?.connections.map((connection) => (
            <Card key={connection.id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <PlatformIcon platform={connection.platform} />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{connection.displayName}</p>
                    <StatusBadge status={connection.status} />
                  </div>
                  <p className="text-xs text-muted">
                    {PLATFORM_LABELS[connection.platform]} · connected{' '}
                    {formatDateTime(connection.createdAt)}
                  </p>
                  {connection.status === 'needs_reauth' ? (
                    <p className="mt-1 text-xs text-warning">
                      The stored authorization was refused. Reconnect this account to resume
                      distributing.
                    </p>
                  ) : null}
                </div>
              </div>
              <Button
                variant="danger"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate(connection.id)}
              >
                Disconnect
              </Button>
            </Card>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted">
          Disconnecting destroys the stored credentials and retires the targets that used them. Past
          distributions keep their history.
        </p>
      </section>
    </div>
  );
}

function PlatformGrid({
  title,
  description,
  options,
  connected,
  busy,
  onConnect,
}: {
  title: string;
  description: string;
  options: PlatformOption[];
  connected: Set<Platform>;
  busy: boolean;
  onConnect: (option: PlatformOption) => void;
}) {
  return (
    <section>
      <SectionHeading title={title} description={description} />
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {options.map((option) => (
          <Card key={option.platform} className="flex items-center justify-between gap-3 p-3">
            <div className="flex min-w-0 items-center gap-3">
              <PlatformIcon platform={option.platform} />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{option.label}</p>
                <p className="truncate text-xs text-muted">{status(option, connected)}</p>
              </div>
            </div>
            <Button
              variant="secondary"
              disabled={!option.implemented || busy || connected.has(option.platform)}
              onClick={() => onConnect(option)}
            >
              {buttonLabel(option, connected)}
            </Button>
          </Card>
        ))}
      </div>
    </section>
  );
}

function status(option: PlatformOption, connected: Set<Platform>): string {
  if (connected.has(option.platform)) return 'Connected';
  if (!option.implemented) return 'Coming soon';
  return option.connectionMode === 'instant' ? 'No setup needed' : 'Needs authorization';
}

function buttonLabel(option: PlatformOption, connected: Set<Platform>): string {
  if (connected.has(option.platform)) return 'Enabled';
  if (!option.implemented) return 'Coming soon';
  return option.connectionMode === 'instant' ? 'Enable' : 'Connect';
}
